import React from 'react';
import { useTranslation } from 'react-i18next';
import './LastRunContinuity.css';

/**
 * LastRunContinuity — J19, Issue #1926 (D-080 re-scope,
 * specs/issue-1926-exit-loop-segment-c-spec.md). Closes frictions C4/C5:
 * the coach's "do it again" advice has no corresponding action once a
 * guest lands back on the homepage after a finished conversation.
 *
 * Renders only when a fresh (unexpired) `last_run` token is passed in —
 * see ../../utils/lastRunContinuity.js for the read/write/TTL logic.
 *
 * No suggested-next-scenario recommendation: `recommended_next` doesn't
 * exist on any response the frontend holds today (verified — see that
 * util's header), and the spec's own fallback for that case (Unknown #3)
 * is to ship without one rather than invent a field. The CTA below just
 * points at the scenario list already on this page, which is "something to
 * do next" without claiming it's a reasoned pick.
 *
 * Per spec's SCOPE NOTE: guest-only. The caller (HomePageV2.jsx) is
 * responsible for the `user?.is_guest` gate — this component only decides
 * whether to render given a token, so it stays testable without AuthContext.
 */
function LastRunContinuity({ lastRun, onBrowse }) {
  const { t } = useTranslation();

  if (!lastRun) return null;

  const scenarioLabel = lastRun.scenarioName
    || t('exitLoop.continuity.scenarioFallback', 'that conversation');

  return (
    <section
      className="gd-last-run-continuity v2-fade-up"
      data-testid="last-run-continuity"
      aria-label={t('exitLoop.continuity.eyebrow', 'Welcome back')}
    >
      <div className="gd-last-run-continuity__card">
        <p className="gd-last-run-continuity__eyebrow">
          {t('exitLoop.continuity.eyebrow', 'Welcome back')}
        </p>
        <p className="gd-last-run-continuity__body">
          {t('exitLoop.continuity.body', {
            defaultValue: 'You just practised "{{scenario}}". Ready for another conversation?',
            scenario: scenarioLabel,
          })}
        </p>
        <button
          type="button"
          className="gd-last-run-continuity__cta"
          data-testid="last-run-continuity-cta"
          onClick={onBrowse}
        >
          {t('exitLoop.continuity.cta', 'Browse more scenarios')}
        </button>
      </div>
    </section>
  );
}

export default LastRunContinuity;
